import React, { useEffect, useState } from "react";
import { Button } from "../../shadcn/components/ui/button";
import { Textarea } from "../../shadcn/components/ui/textarea";
import { DateTime } from "luxon";
import CustomModalLayoutV2 from "../bidder/Tabs/auctionFloor/components/CustomModalLayoutV2";
import { approveApprovalRequest, rejectApprovalRequest } from "services";

interface DDApprovalComponentProps {
  data: any;
  encryptedKey: string;
}

export default function DDApprovalComponent({
  data,
  encryptedKey,
}: DDApprovalComponentProps) {
  const [loading, setLoading] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [isDDProcessed, setIsDDProcessed] = useState(false);
  const [status, setStatus] = useState<"approved" | "rejected" | null>(null);

  const handleApprove = async () => {
    try {
      setLoading(true);
      const response = await approveApprovalRequest({ token: encryptedKey });
      console.log("response: ", response);
      setIsDDProcessed(true);
      setStatus("approved");
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectRemark.trim()) {
      return; // Don't proceed if remark is empty
    }
    try {
      setLoading(true);
      const response = await rejectApprovalRequest({
        token: encryptedKey,
        remarks: rejectRemark,
      });
      console.log("response: ", response);
      setIsRejectModalOpen(false);
      setRejectRemark("");
      setIsDDProcessed(true);
      setStatus("rejected");
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = () => {
    setIsRejectModalOpen(true);
  };

  if (
    isDDProcessed ||
    data.status === "approved" ||
    data.status === "rejected"
  ) {
    const currentStatus = status || data.status;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            {currentStatus === "approved"
              ? "Demand Draft Approved"
              : "Demand Draft Rejected"}
          </h2>
          <p className="text-gray-600 mb-6">
            The bidder has been notified about your decision.
          </p>
          <p className="text-sm text-gray-500">You can now close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center py-4 px-4 border-[1px] rounded-sm font-openSans">
      <div className="bg-white w-full px-0 sm:px-4 py-2 md:px-8">
        <h3 className="text-xl font-semibold text-brand-textBlack mb-6 text-center md:text-left">
          Demand Draft Approval
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-y-[1px] py-4 mb-4">
          <div className="px-2 border-b md:border-b-[0px] md:border-r">
            <div>
              <h4 className="text-md font-semibold text-brand-textBlack mb-3">
                Bidder Details
              </h4>
              <ul className="space-y-2 text-brand-textBlack text-sm pb-4 border-b">
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bidder Name:
                  </span>{" "}
                  {data?.data?.name || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bidder Mobile:
                  </span>{" "}
                  {data?.data?.mobile_number ? data?.data?.mobile_number : ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bidder Email id:
                  </span>{" "}
                  {data?.data?.email ? data?.data?.email : ""}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-md font-semibold text-brand-textBlack mb-3">
                Event Details
              </h4>
              <ul className="space-y-2 text-brand-textBlack text-sm pb-4 border-b">
                <li>
                  <span className="font-medium text-brand-orange mr-2 ">
                    Event Name:
                  </span>{" "}
                  <span className="capitalize text-xs font-semibold tracking-wide">
                    {data?.data?.event_name || ""}
                  </span>
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Event Id:
                  </span>{" "}
                  <a
                    href={`${
                      process.env.NEXT_PUBLIC_BIDDER_DB_URL?.includes(
                        "auctionbazaar"
                      )
                        ? `https://www.auctionbazaar.com/auction-properties/${data?.data?.property_listing_id}`
                        : `https://quickbid.in/auction-properties/${data?.data?.property_listing_id}`
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline cursor-pointer text-xs font-semibold tracking-wider text-brand-headingBlue"
                  >
                    {data?.data?.event_listing_id || ""}
                  </a>
                </li>
                <li className="capitalize">
                  <span className="font-medium text-brand-orange mr-2">
                    Borrower Name:
                  </span>{" "}
                  {data?.data?.borrower_name || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    EMD Last Date:
                  </span>{" "}
                  {data?.data?.emd_last_datetime || ""}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Auction start Date:
                  </span>{" "}
                  {data?.data?.auction_start_datetime || ""}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-md font-semibold text-brand-textBlack mb-3">
                Payment Details
              </h4>
              <ul className="space-y-2 text-brand-textBlack text-sm pb-4">
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    DD Number:
                  </span>{" "}
                  {data?.data?.transaction_id}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Amount:
                  </span>{" "}
                  ₹ {data?.data?.transaction_amount}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Bank:
                  </span>{" "}
                  {data?.data?.emd_paid_from}
                </li>
                <li>
                  <span className="font-medium text-brand-orange mr-2">
                    Submission Date:
                  </span>{" "}
                  {data?.data?.emd_paid_date
                    ? DateTime.fromISO(data?.data?.emd_paid_date)
                        .setZone("Asia/Kolkata")
                        .toFormat("dd MMM yyyy")
                    : ""}
                </li>
              </ul>
            </div>
          </div>

          {/* DD Image */}
          <div className="relative py-4 md:py-0">
            <div className="absolute right-0 flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-brand-headingBlue"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
              <a
                href={`${
                  data?.data?.emd_payment_docs?.[0]
                }?dl=demand-draft-${Date.now()}.jpg`}
                // target="_blank"
                // rel="noopener noreferrer"
                download={`demand-draft-${Date.now()}.jpg`}
                className="inline-flex text-sm tracking-wide font-openSans items-center text-brand-headingBlue hover:underline"
              >
                Download
              </a>
            </div>
            <div className="flex justify-center md:justify-end px-2 mt-4">
              <img
                src={data?.data?.emd_payment_docs?.[0]}
                alt="Demand Draft"
                className="w-full h-auto max-h-[400px] min-h-[200px] object-contain"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons Section */}
        <div className="sticky bottom-0 left-0 bg-white p-2 md:p-0 border-t md:border-t-0 rounded-lg md:rounded-none w-full md:static">
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 md:mt-6">
            <Button
              className="w-full"
              disabled={loading}
              onClick={async () => {
                await handleApprove();
              }}
            >
              Approve
            </Button>
            <Button
              variant="ghost"
              disabled={loading}
              className="w-full bg-brand-bgGrey"
              onClick={handleRejectClick}
            >
              Reject
            </Button>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <CustomModalLayoutV2
        open={isRejectModalOpen}
        onClose={() => {
          if (!loading) {
            setIsRejectModalOpen(false);
            setRejectRemark("");
          }
        }}
        blurBackground={true}
        className="!w-full mx-2 sm:mx-4 md:mx-6 max-w-md overflow-hidden"
        childrenClassName="p-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b flex-shrink-0 bg-white">
            <h3 className="text-lg sm:text-xl font-semibold text-brand-textBlack">
              Reject Demand Draft Request
            </h3>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remark <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Please provide a reason for rejection"
                value={rejectRemark}
                onChange={(e) => setRejectRemark(e.target.value)}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded min-h-[100px]"
                disabled={loading}
              />
              {!rejectRemark.trim() && (
                <p className="text-xs text-red-500 mt-1">
                  Remark is required to reject the request
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-white flex-shrink-0 flex gap-3">
            <Button
              onClick={() => {
                setIsRejectModalOpen(false);
                setRejectRemark("");
              }}
              className="flex-1"
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={loading || !rejectRemark.trim()}
            >
              {loading ? "Rejecting..." : "Confirm Reject"}
            </Button>
          </div>
        </div>
      </CustomModalLayoutV2>
    </div>
  );
}
